import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiSettings } from '../config/api-settings';
import { MilaMultiSelectFetcher, MilaMultiSelectPage } from '../../shared/mila-multi-select/mila-multi-select-option.model';
import { ISO_LANGUAGES } from '../data/iso-languages';

const PAGE_SIZE = 20;

type ResourceKind = 'tags' | 'source_categories' | 'saved_searches' | 'sources' | 'collections' | 'thesaurus_classes';

/**
 * Backs the six filter rows (everything except Knowledge Base, which has
 * no listing to search it's a single fixed value, not a picker) in
 * /rag, /answer, /search, /similar and /image's "Filter cikisi content
 * by" section, via MilaMultiSelect.
 *
 * All six go through the one real, confirmed endpoint
 * GET {fullPath}cikisi/resources?kinds=<kind>&page=&per_page=&q= kind
 * being one of tags / source_categories / saved_searches / sources /
 * collections / thesaurus_classes. "Sources" and "Collections" are the
 * backend names behind what the UI calls "Source IDs" and "Knowledge
 * Topics" respectively same resource, different label at the two
 * layers, same as elsewhere in this app (e.g. camelCase form controls vs
 * snake_case wire fields).
 *
 * All six kinds are flat no parent/child structure server-side, unlike
 * this service's previous guess for "Knowledge Topics"/"Thesaurus /
 * classes" (was modeled as hierarchical, with mock parent/child data).
 * MilaMultiSelectHierarchy has nothing left to back now see rag.ts (and
 * answer/search/similar/image) for FILTER_ROW_META switching those two
 * rows from 'hierarchy' to 'flat'.
 *
 * Response shape is confirmed keyed by the requested kind, not a flat
 * `items` array `{ <kind>: { items: [{id, name}], page, per_page,
 * total } }`, e.g. `{ tags: { items: [...], page: 1, per_page: 25,
 * total: 402 } }`. page/per_page are 1-indexed (the sample's first page
 * is `"page": 1`), unlike MilaMultiSelectFetcher's own 0-indexed `page`
 * param see search() for the +1 that bridges the two.
 */
@Injectable({ providedIn: 'root' })
export class CikisiResourcesService {
  constructor(private http: HttpClient) {}

  searchTags: MilaMultiSelectFetcher = (query, page) => this.search('tags', query, page);
  searchSourceCategories: MilaMultiSelectFetcher = (query, page) => this.search('source_categories', query, page);
  searchSavedSearches: MilaMultiSelectFetcher = (query, page) => this.search('saved_searches', query, page);
  searchSourceIds: MilaMultiSelectFetcher = (query, page) => this.search('sources', query, page);
  searchKnowledgeTopics: MilaMultiSelectFetcher = (query, page) => this.search('collections', query, page);
  searchThesaurusClasses: MilaMultiSelectFetcher = (query, page) => this.search('thesaurus_classes', query, page);

  /** Backs the "Language search" picker the ISO 639-1 list, a genuinely
   *  static, non-account-specific dataset, so this filters/paginates it
   *  locally instead of hitting an endpoint (nothing to confirm with
   *  backend here, unlike the six resources above). */
  searchLanguages: MilaMultiSelectFetcher = (query, page) => {
    const filtered = query
      ? ISO_LANGUAGES.filter(l => l.name.toLowerCase().includes(query.toLowerCase()))
      : ISO_LANGUAGES;
    const start = page * PAGE_SIZE;
    const slice = filtered.slice(start, start + PAGE_SIZE);

    return of({
      items: slice.map(l => ({ id: l.code, label: l.name })),
      hasMore: start + PAGE_SIZE < filtered.length,
    });
  };

  private search(kind: ResourceKind, query: string, page: number): Observable<MilaMultiSelectPage> {
    // +1: MilaMultiSelectFetcher's `page` is 0-indexed, the backend's is 1-indexed.
    let params = new HttpParams().set('kinds', kind).set('page', page + 1).set('per_page', PAGE_SIZE);
    if (query) params = params.set('q', query);

    return this.http.get<any>(`${ApiSettings.fullPath}cikisi/resources`, { params }).pipe(
      map(res => this.normalize(res, kind)),
    );
  }

  private normalize(res: any, kind: ResourceKind): MilaMultiSelectPage {
    const resource = res?.[kind] ?? {};
    const items: any[] = resource.items ?? [];
    const page: number = resource.page ?? 1;
    const perPage: number = resource.per_page ?? PAGE_SIZE;
    const total: number = resource.total ?? items.length;

    return {
      items: items.map(r => ({ id: r.id, label: r.name ?? r.label })),
      hasMore: page * perPage < total,
    };
  }
}
